import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { RecruiterEntryRoutingService } from '@core/security/services/recruiter-entry-routing.service';
import { BillingState } from '@core/store/billing/billing.state';
import { Store } from '@ngxs/store';

/**
 * SDD 2026-08-07-recruiter-subscription-first-entry, F2, design D-4/D-5/D-6.
 *
 * Reemplaza a `onboardingCompletedGuard` en las superficies protegidas
 * (`creditos`, `vacantes`, `notificaciones`, `solicitudes-entrevista`).
 * `/reclutador/plan` (su destino de rebote) MUST NOT llevar este guard ni
 * ningún otro que pueda redirigir fuera de él — es el anti-bucle
 * estructural de D-5.
 *
 * INVARIANTE RECTOR (design §1): este guard es UX, NO la frontera de
 * seguridad. Ningún recurso de valor se protege con él — las bolsas de
 * crédito y el cupo de vacantes siguen defendidos server-side (409
 * `INSUFFICIENT_CREDITS` / `VACANCY_SLOT_OVERAGE`). Por eso FALLA ABIERTO
 * ante `subscriptionLoadFailed` (D-6): bloquear a TODA la base instalada
 * por una caída de `GET /billing/me/subscription` es peor que dejar pasar
 * a un recién registrado a una pantalla que no puede gastar nada.
 *
 * D-11 — el acceso NUNCA se decide con el reloj del cliente: `canEnterProduct`
 * (BillingState) ya compone `hasLiveSubscription() || withinPaidTail() ||
 * grandfathered()`, los tres resueltos con hechos publicados por el backend.
 * Este archivo no debe instanciar la clase `Date` del entorno del cliente —
 * verificación estática D-11 (`rg` sobre el constructor correspondiente).
 *
 * post-verify fix (W1) — este guard es el ÚNICO punto de las 4 superficies protegidas que
 * corre en un deep-link/hard-refresh en frío (nunca pasa por `resolveAuthenticatedRoute()`,
 * que solo corre tras `no-auth.guard.ts`/`auth.page.ts` en login). El guard que reemplazó
 * (`onboardingCompletedGuard`) esperaba `ensureProfileLoaded()` en las cuatro; restaurado aquí
 * EN PARALELO con `ensureSubscriptionLoaded()` (`Promise.all`, no serial) porque son dos cargas
 * independientes.
 */
export const subscriptionGuard: CanActivateFn = async (): Promise<boolean | UrlTree> => {
  const router = inject(Router);
  const store = inject(Store);
  const entryRouting = inject(RecruiterEntryRoutingService);

  await Promise.all([entryRouting.ensureSubscriptionLoaded(), entryRouting.ensureProfileLoaded()]);

  if (store.selectSnapshot(BillingState.canEnterProduct)) {
    return true;
  }

  if (store.selectSnapshot(BillingState.subscriptionLoadFailed)) {
    return true;
  }

  return router.createUrlTree(['/reclutador/plan']);
};
